import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Snackbar,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {useTranslation} from "react-i18next";
import PayrollPeriodMobile from "./mobile/PayrollPeriodMobile";
import DeleteConfirmDialog from "./dialogs/DeleteConfirmDialog";
import LockPeriodConfirmDialog from "./dialogs/LockPeriodConfirmDialog";
import CreatePeriodDialog from "./dialogs/CreatePeriodDialog";
import PayrollPeriodListPanel from "./PayrollPeriodListPanel";
import PayrollPeriodStatusPanel from "./PayrollPeriodStatusPanel";
import PayrollPeriodEmployeesTable from "./content/PayrollPeriodEmployeesTable";
import {usePayrollPeriod} from "../../../hooks/usePayrollPeriod";
import {useAppNavigation} from "../../../context/NavigationContext";
import {usePayrollTabSelector} from "../PayrollTabContext";
import {formatPeriodLabel} from "../../../utils/periodLabel";

export default function PayrollPeriod(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {navigate} = useAppNavigation();
    const payrollTabs = usePayrollTabSelector();

    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

    const {
        branches,
        selectPeriod,
        handleRefreshContracts,
        evidenceTotal,
        evidencePending,
        evidenceLoading,
        calculationAvailable,
        contractsLastSyncedAt,
        selectedPeriod,
        selectedPeriodValue,
        selectedYear,
        allPeriods,
        creatingPeriod,
        contracts,
        contractsLoading,
        contractsPage,
        contractsRowsPerPage,
        snapshotCreating,
        showSuccessMessage,
        setShowSuccessMessage,
        successMessageText,
        snapshotError,
        setSnapshotError,
        confirmDialogOpen,
        setConfirmDialogOpen,
        currentAction,
        setCurrentAction,
        deleteConfirmDialogOpen,
        setDeleteConfirmDialogOpen,
        isDeleting,
        years,
        months,
        selectedMonth,
        handleYearChange,
        handleMonthChange,
        handleContractsChangePage,
        handleContractsChangeRowsPerPage,
        handleGenerateSnapshot,
        handleLockUnlock,
        handleCreatePeriod,
        handleDeleteSnapshot,
        isLockedState,
        isOpenState,
        lockButtonText,
        lockButtonColor,
    } = usePayrollPeriod();

    const periodLabel = formatPeriodLabel(t, selectedPeriod, selectedPeriodValue);
    const goToEvidence = () => (payrollTabs ? payrollTabs.selectTab('workEvidence') : navigate('Work Evidence'));

    return (
        <Box sx={{width: "100%"}}>
            <Snackbar
                open={showSuccessMessage}
                autoHideDuration={3000}
                onClose={() => setShowSuccessMessage(false)}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
            >
                <Alert severity="success" onClose={() => setShowSuccessMessage(false)} sx={{width: "100%"}}>
                    {successMessageText}
                </Alert>
            </Snackbar>

            {snapshotError && (
                <Alert severity="error" onClose={() => setSnapshotError(null)} sx={{mb: 2}}>
                    {snapshotError}
                </Alert>
            )}

            {isMobile ? (
                <PayrollPeriodMobile
                    branches={branches}
                    selectPeriod={selectPeriod}
                    handleRefreshContracts={handleRefreshContracts}
                    evidenceTotal={evidenceTotal}
                    evidencePending={evidencePending}
                    evidenceLoading={evidenceLoading}
                    calculationAvailable={calculationAvailable}
                    contractsLastSyncedAt={contractsLastSyncedAt}
                    selectedPeriod={selectedPeriod}
                    selectedPeriodValue={selectedPeriodValue}
                    selectedYear={selectedYear}
                    allPeriods={allPeriods}
                    contracts={contracts}
                    contractsLoading={contractsLoading}
                    years={years}
                    handleYearChange={handleYearChange}
                    snapshotCreating={snapshotCreating}
                    handleGenerateSnapshot={handleGenerateSnapshot}
                    lockButtonText={lockButtonText}
                    isLockedState={isLockedState}
                    onLockUnlockClick={() => {
                        setCurrentAction(isOpenState ? "lock" : "unlock");
                        setConfirmDialogOpen(true);
                    }}
                    onDeleteSnapshotClick={() => setDeleteConfirmDialogOpen(true)}
                    onContinueToEvidence={goToEvidence}
                    onOpenCreateDialog={() => setCreateDialogOpen(true)}
                    periodLabel={periodLabel}
                />
            ) : (
                <Stack spacing={2.5}>
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<AddIcon/>}
                            onClick={() => setCreateDialogOpen(true)}
                            sx={{borderWidth: '1px', borderRadius: '8px', '&:hover': {borderWidth: '1px'}}}
                        >
                            {t('payroll.period.createButton')}
                        </Button>
                    </Stack>

                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "2fr 3fr"}, gap: 2.5}}>
                        <PayrollPeriodListPanel
                            allPeriods={allPeriods}
                            selectedYear={selectedYear}
                            years={years}
                            handleYearChange={handleYearChange}
                            selectedPeriod={selectedPeriod}
                            onSelectPeriod={selectPeriod}
                        />
                        <PayrollPeriodStatusPanel
                            selectedPeriod={selectedPeriod}
                            selectedPeriodValue={selectedPeriodValue}
                            isLockedState={isLockedState}
                            contracts={contracts}
                            contractsLoading={contractsLoading}
                            branches={branches}
                            evidenceTotal={evidenceTotal}
                            evidencePending={evidencePending}
                            evidenceLoading={evidenceLoading}
                            calculationAvailable={calculationAvailable}
                            contractsLastSyncedAt={contractsLastSyncedAt}
                            handleRefreshContracts={handleRefreshContracts}
                            snapshotCreating={snapshotCreating}
                            handleGenerateSnapshot={handleGenerateSnapshot}
                            lockButtonText={lockButtonText}
                            onLockUnlockClick={() => {
                                setCurrentAction(isOpenState ? "lock" : "unlock");
                                setConfirmDialogOpen(true);
                            }}
                            onDeleteSnapshotClick={() => setDeleteConfirmDialogOpen(true)}
                        />
                    </Box>

                    <Card variant="outlined" sx={{borderRadius: 1}}>
                        <CardContent>
                            <Typography variant="subtitle1" fontWeight={700} sx={{mb: 2, textAlign: "left"}}>
                                {t('payroll.period.employees.title')}
                            </Typography>
                            <PayrollPeriodEmployeesTable
                                contracts={contracts}
                                contractsLoading={contractsLoading}
                                contractsPage={contractsPage}
                                contractsRowsPerPage={contractsRowsPerPage}
                                handleContractsChangePage={handleContractsChangePage}
                                handleContractsChangeRowsPerPage={handleContractsChangeRowsPerPage}
                                periodKey={selectedPeriod}
                            />
                            <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    endIcon={<ArrowForwardIcon/>}
                                    onClick={goToEvidence}
                                    disabled={!selectedPeriod}
                                >
                                    {t('payroll.period.continueToEvidence')}
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            )}

            <CreatePeriodDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                years={years}
                months={months}
                handleYearChange={handleYearChange}
                handleMonthChange={handleMonthChange}
                handleCreatePeriod={handleCreatePeriod}
                creatingPeriod={creatingPeriod}
            />

            <LockPeriodConfirmDialog
                open={confirmDialogOpen}
                currentAction={currentAction}
                lockButtonText={lockButtonText}
                lockButtonColor={lockButtonColor}
                isOpenState={isOpenState}
                onCancel={() => setConfirmDialogOpen(false)}
                onConfirm={async () => {
                    setConfirmDialogOpen(false);
                    await handleLockUnlock();
                }}
            />

            <DeleteConfirmDialog
                open={deleteConfirmDialogOpen}
                isDeleting={isDeleting}
                onCancel={() => setDeleteConfirmDialogOpen(false)}
                onConfirm={async () => {
                    setDeleteConfirmDialogOpen(false);
                    await handleDeleteSnapshot();
                }}
            />
        </Box>
    );
}
